import { useContext, useMemo } from "react";
import { sortBy } from "lodash";
import { DOCUMENT_UPLOAD_DETAIL_TAB_NAME } from "common/constants";
import { pipLink } from "common/routing";
import { categorizeFIRMsByJurisdiction } from "common/utils/firms/index";
import {
  CERTIFICATE_VIEW_FIRM_WARNINGS,
  floodzoneOrCoastalAIntersectionsPerFirm,
  getWarningsForFirms,
  propertyInMultiplePanels,
} from "common-client/utils/firmInfoWarnings";
import { editableInformationForFIRMs } from "common-client/utils/firms";
import {
  CertificateUploadStatus,
  Datum,
  ErrorCode,
  ErrorDetectionEligibilityStatus,
  FIRMJurisdiction,
  GetDocumentUploadQuery,
} from "../../../../generated/graphql";
import { spacing } from "../../../../stitches.config";
import { FIRMWarningsBanner } from "../../../AddressPanel/PropertyOverview/FIRMInfo/FIRMWarningsBanner";
import { AuthContext } from "../../../Authorization/AuthContext";
import { StyledReactRouterLink } from "../../../Common/__styles__/Typography";
import { EmptyState } from "../../../Common/EmptyState";
import { RelevantFIRMs } from "../../../FIRM/RelevantFIRMs";
import { IssueBanner } from "../../../Issues";
import { CertificateType } from "../../__queries__";
import { CertificateFormFieldDottableKeys } from "../../Forms/ElevationCertificates/CertificatesInputs";
import { fieldsForCertificate } from "../../Forms/ElevationCertificates/ExtractedFields";
import MobileDocumentLink from "../../MobileDocumentLink";
import { DocumentUploadContext } from "../../Tabs";
import { useShowLocationTab } from "../../useShowLocationTab";
import {
  Checklist,
  ErrorContainer,
  Heading,
  OverlapWarning,
  ValidationErrorsContainer,
} from "./__styles__";
import ValidationError from "./ValidationError";

type FIRMs = NonNullable<
  NonNullable<GetDocumentUploadQuery["documentUpload"]>["elevationCertificate"]
>["firms"];
type ValidationErrors = CertificateType["validationErrors"];
type ValidationError = NonNullable<
  NonNullable<ValidationErrors>["data"]
>[number];
type Coordinates = [number, number];

const PANELS_OVERLAP_ERROR_CODES = [
  ErrorCode.FIRM_PANEL_EFFECTIVE_DATE_MISMATCH,
  ErrorCode.PANEL_NUMBER_MISMATCH,
  ErrorCode.SUFFIX_MISMATCH,
];

const REGULATORY_ZONES_OVERLAP_ERROR_CODES = [
  ErrorCode.REGULATORY_LFE_BELOW_DFE,
  ErrorCode.REGULATORY_LFEHAG_BELOW_DFE,
  ErrorCode.REGULATORY_MACHINERY_BELOW_AODFE,
  ErrorCode.REGULATORY_MACHINERY_BELOW_DFE,
];

const NFIP_ZONES_OVERLAP_ERROR_CODES = [
  ErrorCode.BFE_FIRM_MISMATCH,
  ErrorCode.C2C_BLANK_OR_NA,
  ErrorCode.FLOODZONE_FIRM_MISMATCH,
  ErrorCode.NFIP_LFE_BELOW_BFE,
  ErrorCode.NFIP_LFEHAG_BELOW_BFE,
  ErrorCode.NFIP_MACHINERY_BELOW_AOBFE,
  ErrorCode.NFIP_MACHINERY_BELOW_BFE,
];

const getFirmIdForError = ({
  error,
  firms,
}: {
  error: ValidationError;
  firms: FIRMs;
}) => {
  let jurisdiction: Maybe<FIRMJurisdiction> = null;

  if (NFIP_ZONES_OVERLAP_ERROR_CODES.includes(error.errorCode)) {
    jurisdiction = FIRMJurisdiction.NFIP;
  } else if (REGULATORY_ZONES_OVERLAP_ERROR_CODES.includes(error.errorCode)) {
    jurisdiction = FIRMJurisdiction.REGULATORY;
  }

  if (jurisdiction) {
    return firms.find(firm => firm.jurisdictions.includes(jurisdiction!))?.id;
  } else {
    return null;
  }
};

const ValidationErrors = ({
  validationErrors,
  setTab,
  isEligible,
  accountDatums,
}: {
  setTab: (tab: DOCUMENT_UPLOAD_DETAIL_TAB_NAME) => void;
  validationErrors: ValidationErrors;
  isEligible: boolean;
  accountDatums: Array<Datum>;
}) => {
  const { documentUpload } = useContext(DocumentUploadContext);

  const errors = validationErrors?.data || [];
  const firmEditUrl = pipLink({
    propertyId: documentUpload.property?.id,
    address: documentUpload.property?.fullAddress ?? "",
    lng: documentUpload.property?.longitude.toString() ?? "",
    lat: documentUpload.property?.latitude.toString() ?? "",
  });

  let body;

  const firms =
    documentUpload.elevationCertificate?.firms.filter(
      firm => firm.useForRegulation,
    ) ?? [];

  const intersections = useMemo(() => {
    return editableInformationForFIRMs({ firms, accountDatums });
  }, []);

  const floodzoneIntersectionsPerFirm =
    floodzoneOrCoastalAIntersectionsPerFirm(intersections);

  const multiplePanels = propertyInMultiplePanels(intersections);

  // We want errors to be orderd the same way that Extracted Fields are
  const sortedFields = fieldsForCertificate(
    documentUpload.elevationCertificate!,
  ).map(m => m.name);

  const sortedErrors = sortBy(
    errors,
    error => {
      const valueAttr = error.valueAttr[0]!;
      let index = sortedFields.indexOf(
        valueAttr as CertificateFormFieldDottableKeys,
      );
      // We have a bug where for fields that use `buildNumericNAValidation` validator in the backend,
      // it returns the name of the flat field instead of the dottable. Those return -1 because they aren't
      // in sortedFields. All those fields happen to be from `buildingElevationInfo`, so we can just manually tack it on
      if (index === -1) {
        index = sortedFields.indexOf(
          `buildingElevationInfo.${valueAttr}.value` as CertificateFormFieldDottableKeys,
        );
      }
      return index;
    },
    error => {
      return error.type === "error" ? 0 : 1;
    },
  );

  body = sortedErrors.map(error => {
    const { note, errorCode, id } = error;
    const errorFirmId = getFirmIdForError({
      error,
      firms: documentUpload.elevationCertificate?.firms ?? [],
    });

    const zonesIntersect = errorFirmId
      ? floodzoneIntersectionsPerFirm[errorFirmId]
      : false;
    const panelsIntersect =
      multiplePanels && PANELS_OVERLAP_ERROR_CODES.includes(errorCode);

    const showNote = (zonesIntersect || panelsIntersect) && note;

    return (
      <ValidationError
        key={id}
        error={{ ...error, note: showNote ? note : null }}
        label={error.field}
        value={error.value}
        setTab={setTab}
      />
    );
  });

  const firmsWithWarnings = getWarningsForFirms({
    firms,
    editable: intersections,
    showCoastalA: true,
  });

  return (
    <ValidationErrorsContainer data-testid="validation-errors">
      {body}

      {isEligible && (
        <OverlapWarning>
          <FIRMWarningsBanner
            firms={firmsWithWarnings}
            warningTextConfig={CERTIFICATE_VIEW_FIRM_WARNINGS}
            propertyId={documentUpload.property?.id}
            compactList={true}
            additionalReplacements={{
              "{PROPERTY_LINK}": (
                <StyledReactRouterLink
                  to={firmEditUrl}
                  style={{
                    color: "inherit",
                    textDecoration: "underline",
                    fontWeight: "inherit",
                  }}
                >
                  View FIRM Info here.
                </StyledReactRouterLink>
              ),
            }}
          />
        </OverlapWarning>
      )}
    </ValidationErrorsContainer>
  );
};

export type ErrorsProps = {
  fileUrl: string;
  setTab: (tab: DOCUMENT_UPLOAD_DETAIL_TAB_NAME) => void;
};

export default ({ fileUrl, setTab }: ErrorsProps) => {
  const { documentUpload } = useContext(DocumentUploadContext);
  const { isPublic, account } = useContext(AuthContext);
  const showLocationTab = useShowLocationTab(documentUpload);
  const issueProps = {
    certificateId: documentUpload.elevationCertificate?.id,
    address: documentUpload.property?.fullAddress ?? "",
    coordinates: [
      documentUpload.property?.longitude,
      documentUpload.property?.latitude,
    ] as Coordinates,
  };

  const hasElevationCertificate = Boolean(documentUpload.elevationCertificate);
  documentUpload.elevationCertificate!;
  const hasErrors = Boolean(
    documentUpload.elevationCertificate?.validationErrors?.data?.length,
  );
  const errorDetectionEligibility =
    documentUpload.elevationCertificate?.errorDetectionEligibility;

  const isEligible =
    errorDetectionEligibility?.status ===
    ErrorDetectionEligibilityStatus.ELIGIBLE;

  const showMissingElevationCertificate = !hasElevationCertificate;
  const showErrorDetectionEligibility =
    hasElevationCertificate && errorDetectionEligibility && !hasErrors;

  const eligibilityNote = useMemo(() => {
    if (
      errorDetectionEligibility?.status ===
        ErrorDetectionEligibilityStatus.MISSING_PROPERTY &&
      showLocationTab
    ) {
      return `${errorDetectionEligibility.note} Visit the location tab to assign it to a property.`;
    }
    return errorDetectionEligibility?.note;
  }, [errorDetectionEligibility, showLocationTab]);

  const { nfip, regulatory } = categorizeFIRMsByJurisdiction({
    firms: documentUpload.elevationCertificate?.firms ?? [],
  });

  return (
    <ErrorContainer data-testid="error-container">
      {showMissingElevationCertificate && (
        <EmptyState
          icon="missing-incomplete"
          title="Elevation Certificate is processing"
          body="Please check back later for extracted fields and error detection."
          paddingTop={spacing["3xl"].value}
        />
      )}
      {showErrorDetectionEligibility && (
        <EmptyState
          icon={isEligible ? "no-activity-hot-air-balloon" : "error-alert"}
          title={errorDetectionEligibility.heading}
          body={eligibilityNote}
          paddingTop={spacing["3xl"].value}
        />
      )}
      {(isEligible || hasErrors) && (
        <RelevantFIRMs
          title="Issues have been processed using the following FIRM data:"
          firms={[
            {
              label: "NFIP FIRM",
              value: nfip?.name,
            },
            {
              label: "Regulatory FIRM",
              value: regulatory?.name,
            },
          ]}
          color="contentSecondary"
          paddingTop={
            showMissingElevationCertificate || showErrorDetectionEligibility
              ? spacing["2xl"].value
              : undefined
          }
        />
      )}
      {hasElevationCertificate && hasErrors && (
        <>
          <ValidationErrors
            isEligible={isEligible}
            validationErrors={
              documentUpload.elevationCertificate?.validationErrors
            }
            setTab={setTab}
            accountDatums={account?.datums ?? []}
          />
          <Checklist data-testid="error-issues-guide">
            <Heading>
              Our Issues feature does not replace a file review process from a
              Certified Floodplain Manager. Please read through our{" "}
              <a
                href="https://forerunner.helpscoutdocs.com/article/35-issues-guide"
                target="_blank"
              >
                Issues Guide
              </a>{" "}
              to ensure that you understand the full capabilities of the
              feature. Here are a few items that need to be checked for manually
              on each Elevation Certificate:
            </Heading>
            <ul>
              <li>
                <span>
                  Accuracy and formatting of Latitude & Longitude coordinates
                </span>
              </li>
              <li>
                <span>Inclusion of at least 2 photographs</span>
              </li>
              <li>
                <span>Correct Diagram Number selection</span>
              </li>
              <li>
                <span>
                  Accuracy of CBRS & OPA determination on ECs with an expiration
                  year prior to 2026
                </span>
              </li>
              <li>
                <span>Accuracy of FIRM Index Date</span>
              </li>
              <li>
                <span>
                  Presence of surveyor, engineer, or architect stamp & signature
                </span>
              </li>
              <li>
                <span>Accuracy of Section F and G</span>
              </li>
              <li>
                <span>Presence of Section F</span>
              </li>
              <li>
                <span>
                  Presence of Section E and G on ECs with an expiration year
                  prior to 2026
                </span>
              </li>
              <li>
                <span>
                  Attachment of Flood Openings Certification, where applicable
                </span>
              </li>
              <li>
                <span>
                  Attachment of V Zone Certification, where applicable
                </span>
              </li>
              <li>
                <span>Amendments made using a Memo of Correction</span>
              </li>
              <li>
                <span>Presence of breakaway walls</span>
              </li>
              <li>
                <span>
                  Other regulatory issues that are specific to your jurisdiction
                </span>
              </li>
            </ul>
          </Checklist>

          <MobileDocumentLink
            fileUrl={fileUrl}
            documentUpload={documentUpload}
          />

          {!(
            isPublic ||
            documentUpload.certificateUpload?.status ===
              CertificateUploadStatus.PROCESSING
          ) && <IssueBanner {...issueProps} />}
        </>
      )}
    </ErrorContainer>
  );
};
